import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
import { staggerContainer, fadeInUp, displayFont } from './shared';
import type { HomepageStrings } from '../../i18n/homepage';

type TestimonialsStrings = Pick<HomepageStrings, 'testimonials_badge' | 'testimonials_heading_pre' | 'testimonials_heading_highlight'>;

const EN_DEFAULTS: TestimonialsStrings = {
  testimonials_badge: 'Reviews',
  testimonials_heading_pre: 'Loved by couples ',
  testimonials_heading_highlight: 'on their big day',
};

const testimonials = [
  {
    quote: "Honestly this website is absolutely fantastic. Saved me a lot of time. Honestly the time its saved, plus the seating plan. I love that you can also add music which gives your invites a really personal feel. Again thank you so much.",
    name: "Jade H",
    meta: "Bride, September 2027",
  },
  {
    quote: "We were blown away by how easy it was. Our guests kept saying the invite felt so personal — we had it live within an hour of signing up.",
    name: "Hannah & Tom",
    meta: "Married March 2026",
  },
  {
    quote: "Finally a digital invite that doesn't look cheap. The envelope reveal had our guests messaging us before they'd even RSVP'd.",
    name: "Sophie Clarke",
    meta: "Bride, February 2026",
  },
  {
    quote: "We loved that we could do a Save the Date and the full invite in one place. Saved us so much time and our guests found it really easy to use.",
    name: "James & Oliver",
    meta: "Married January 2026",
  },
  {
    quote: "My daughter used Wedsy for her wedding and I was honestly impressed. It looked stunning and every guest knew exactly what was happening on the day.",
    name: "Charlotte Brennan",
    meta: "Mother of the bride, April 2026",
  },
];

export function TestimonialsSection({ strings }: { strings?: TestimonialsStrings }) {
  const s = strings ?? EN_DEFAULTS;
  return (
    <section className="py-20 md:py-32 bg-background overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="text-center mb-12"
        >
          <motion.span
            variants={fadeInUp}
            className="inline-block px-4 py-2 bg-primary/10 rounded-full text-sm font-medium text-primary mb-4"
          >
            {s.testimonials_badge}
          </motion.span>
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl lg:text-5xl text-text-primary mb-4"
            style={displayFont}
          >
            {s.testimonials_heading_pre}<span className="text-primary">{s.testimonials_heading_highlight}</span>
          </motion.h2>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
        >
          {testimonials.map((t) => (
            <motion.div
              key={t.name}
              variants={fadeInUp}
              className="bg-background-card border border-border rounded-2xl p-6 flex flex-col gap-4"
            >
              {/* Stars */}
              <div className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-primary text-primary" />
                ))}
              </div>
              {/* Quote */}
              <p className="text-text-secondary text-sm leading-relaxed flex-1">
                "{t.quote}"
              </p>
              {/* Name */}
              <div>
                <p className="text-sm font-semibold text-text-primary" style={displayFont}>{t.name}</p>
                <p className="text-xs text-text-secondary mt-0.5">{t.meta}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
